import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { RouteScheduleDiffConfirmComponent } from '../route-schedule-diff-confirm/route-schedule-diff-confirm.component';

interface SlotPreset {
  label: string;
  reservedSeats: { [key: string]: number };
  startOffset: { [key: string]: number };
}

@Component({
  selector: 'app-route-schedule-slot-edit',
  templateUrl: './route-schedule-slot-edit.component.html',
  styleUrls: ['./route-schedule-slot-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RouteScheduleSlotEditComponent {
  slotsForm: UntypedFormGroup;
  presets: SlotPreset[] = [];
  error: string = null;

  // The template's Always-assignment period (id + dates). Null for override templates (Manual/
  // DayOfYear/DayOfWeek), which aren't part of the contiguous Always chain and so have no period
  // editing here (they're edited via the override-create flow instead).
  currentPeriod: any = null;

  constructor(
    private fb: UntypedFormBuilder,
    private backofficeService: BackOfficeService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<RouteScheduleSlotEditComponent>,
  ) {
    const slots = this.sortSlotsByTime(this.data.template?.slots || []);
    this.presets = this.extractPresets(slots);
    this.currentPeriod = this.getCurrentPeriod();

    this.slotsForm = this.fb.group({
      name: [this.data.template?.name, Validators.required],
      dateFrom: [this.currentPeriod?.dateFrom, Validators.required],
      dateTo: [this.currentPeriod?.dateTo, Validators.required],
      slots: this.fb.array(slots.map((slot: any) => this.createSlotGroupFromData(slot)))
    });
  }

  private getCurrentPeriod(): any {
    const template = this.data.template;
    if (!template || template.assignmentType !== 'Always' || !template.assignmentId) return null;
    return { id: template.assignmentId, dateFrom: template.assignmentDateFrom, dateTo: template.assignmentDateTo };
  }

  private sortSlotsByTime(slots: any[]): any[] {
    return [...slots].sort((a, b) => this.timeToMinutes(a.Time) - this.timeToMinutes(b.Time));
  }

  private timeToMinutes(time: string): number {
    if (!time) return 0;
    const [hours, minutes] = time.split(':');
    return (Number(hours) || 0) * 60 + (Number(minutes) || 0);
  }

  get slots() {
    return this.slotsForm.get('slots') as UntypedFormArray;
  }

  private extractPresets(slots: any[]): SlotPreset[] {
    const seen = new Map<string, SlotPreset>();
    slots.forEach(slot => {
      const reservedSeats = slot.ReservedSeats || {};
      const startOffset = slot.StartOffset || {};
      const key = JSON.stringify({ reservedSeats, startOffset });
      if (!seen.has(key)) {
        const label = Object.entries(reservedSeats).map(([k, v]) => `${k}: ${v}`).join(', ') || 'Empty preset';
        seen.set(key, { label, reservedSeats, startOffset });
      }
    });
    return Array.from(seen.values());
  }

  private dictToRows(dict: { [key: string]: number }) {
    return Object.entries(dict || {}).map(([name, count]) => this.fb.group({
      name: [name, Validators.required],
      count: [count, [Validators.required, Validators.min(0)]]
    }));
  }

  private rowsToDict(rows: any[]): { [key: string]: number } {
    const dict: { [key: string]: number } = {};
    rows.forEach(row => {
      if (row.name) {
        dict[row.name] = Number(row.count) || 0;
      }
    });
    return dict;
  }

  createSlotGroup() {
    return this.fb.group({
      time: ['', Validators.required],
      reservedSeats: this.fb.array([]),
      startOffset: this.fb.array([])
    });
  }

  createSlotGroupFromData(slot: any) {
    return this.fb.group({
      time: [this.formatTimeForInput(slot.Time), Validators.required],
      reservedSeats: this.fb.array(this.dictToRows(slot.ReservedSeats)),
      startOffset: this.fb.array(this.dictToRows(slot.StartOffset))
    });
  }

  formatTimeForInput(time: string): string {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    return `${hours.padStart(2, '0')}:${minutes || '00'}`;
  }

  getReservedSeats(slotIndex: number) {
    return (this.slots.at(slotIndex).get('reservedSeats') as UntypedFormArray);
  }

  getStartOffset(slotIndex: number) {
    return (this.slots.at(slotIndex).get('startOffset') as UntypedFormArray);
  }

  addSlot() {
    this.slots.push(this.createSlotGroup());
  }

  removeSlot(index: number) {
    this.slots.removeAt(index);
  }

  addEntry(slotIndex: number, target: 'reservedSeats' | 'startOffset') {
    const array = target === 'reservedSeats' ? this.getReservedSeats(slotIndex) : this.getStartOffset(slotIndex);
    array.push(this.fb.group({
      name: ['', Validators.required],
      count: [0, [Validators.required, Validators.min(0)]]
    }));
  }

  removeEntry(slotIndex: number, target: 'reservedSeats' | 'startOffset', entryIndex: number) {
    const array = target === 'reservedSeats' ? this.getReservedSeats(slotIndex) : this.getStartOffset(slotIndex);
    array.removeAt(entryIndex);
  }

  applyPreset(slotIndex: number, preset: SlotPreset) {
    const slotGroup = this.slots.at(slotIndex) as UntypedFormGroup;
    slotGroup.setControl('reservedSeats', this.fb.array(this.dictToRows(preset.reservedSeats)));
    slotGroup.setControl('startOffset', this.fb.array(this.dictToRows(preset.startOffset)));
  }

  save() {
    if (!this.slotsForm.valid) return;

    const name = (this.slotsForm.value.name || '').trim();
    const dateFrom = this.slotsForm.value.dateFrom;
    const dateTo = this.slotsForm.value.dateTo;
    const timeSlots = this.slots.value
      .slice()
      .sort((a: any, b: any) => this.timeToMinutes(a.time) - this.timeToMinutes(b.time))
      .map((slot: any) => ({
        Time: slot.time,
        ReservedSeats: this.rowsToDict(slot.reservedSeats),
        StartOffset: this.rowsToDict(slot.startOffset)
      }));

    const changes = this.buildDiff(name, timeSlots, dateFrom, dateTo);
    if (changes.length === 0) {
      this.dialogRef.close();
      return;
    }

    const dialogRef = this.dialog.open(RouteScheduleDiffConfirmComponent, {
      width: '500px',
      data: { changes }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.performSave(name, timeSlots, dateFrom, dateTo);
      }
    });
  }

  private performSave(name: string, timeSlots: any[], dateFrom: string, dateTo: string) {
    const data: any = {
      id: this.data.template?.id,
      entityId: this.data.template?.entityId,
      name,
      description: this.data.template?.description,
      timeSlots
    };

    if (this.currentPeriod) {
      data.assignmentId = this.currentPeriod.id;
      data.dateFrom = dateFrom;
      data.dateTo = dateTo;
    }

    this.error = null;
    this.backofficeService.addUpdateScheduleAssignment(data).subscribe({
      next: (response) => {
        if (!response) {
          this.error = 'Could not save. A template with this name may already exist for this route.';
          return;
        }
        this.dialogRef.close({ success: true, template: response });
      },
      error: (error) => {
        console.error('Error saving route schedule template:', error);
        this.error = (typeof error?.error === 'string' && error.error) || 'Could not save the template';
      }
    });
  }

  private buildDiff(newName: string, newSlots: any[], newDateFrom?: string, newDateTo?: string): string[] {
    const changes: string[] = [];

    if (this.currentPeriod) {
      if (this.currentPeriod.dateFrom !== newDateFrom) {
        changes.push(`Start date: "${this.currentPeriod.dateFrom}" → "${newDateFrom}"`);
      }
      if (this.currentPeriod.dateTo !== newDateTo) {
        changes.push(`End date: "${this.currentPeriod.dateTo}" → "${newDateTo}"`);
      }
    }

    const oldName = this.data.template?.name;
    if (oldName !== newName) {
      changes.push(`Name: "${oldName}" → "${newName}"`);
    }

    const oldByTime = new Map<string, any>();
    (this.data.template?.slots || []).forEach((s: any) => oldByTime.set(this.formatTimeForInput(s.Time), s));
    const newByTime = new Map<string, any>();
    newSlots.forEach((s: any) => newByTime.set(this.formatTimeForInput(s.Time), s));

    const allTimes = Array.from(new Set([...oldByTime.keys(), ...newByTime.keys()]))
      .sort((a, b) => this.timeToMinutes(a) - this.timeToMinutes(b));

    allTimes.forEach(time => {
      const oldSlot = oldByTime.get(time);
      const newSlot = newByTime.get(time);

      if (!oldSlot) {
        changes.push(`+ Added slot ${time}`);
        return;
      }
      if (!newSlot) {
        changes.push(`− Removed slot ${time}`);
        return;
      }

      this.diffDict(time, 'Reserved seats', oldSlot.ReservedSeats, newSlot.ReservedSeats, changes);
      this.diffDict(time, 'Start offset', oldSlot.StartOffset, newSlot.StartOffset, changes);
    });

    return changes;
  }

  private diffDict(time: string, label: string, oldDict: any, newDict: any, changes: string[]) {
    oldDict = oldDict || {};
    newDict = newDict || {};
    const keys = new Set([...Object.keys(oldDict), ...Object.keys(newDict)]);

    keys.forEach(key => {
      const oldVal = oldDict[key];
      const newVal = newDict[key];
      if (oldVal === undefined) {
        changes.push(`~ ${time}: ${label} "${key}" added (${newVal})`);
      } else if (newVal === undefined) {
        changes.push(`~ ${time}: ${label} "${key}" removed (was ${oldVal})`);
      } else if (oldVal !== newVal) {
        changes.push(`~ ${time}: ${label} "${key}": ${oldVal} → ${newVal}`);
      }
    });
  }
}
